import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	ElementRef,
	input,
	OnDestroy,
	signal,
	WritableSignal,
	inject,
	output,
} from '@angular/core'; // Import input and computed

import * as d3 from 'd3';
import { BarTextStyle, GanttClickEvent, GanttRow, GanttSegment, ParameterValueType } from 'src/app/components/util/ara-bar-chart/gantt.models';
import { AraBarChartComponent } from '../../../../../../util/ara-bar-chart/ara-bar-chart.component';
import { TranslocoDirective } from '@jsverse/transloco';
import { Icons } from '../../../../../../../util/Icons';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

type TimeScale = d3.ScaleTime<number, number>;

@Component({
	selector: 'ara-parameter-value-gantt',
	imports: [AraBarChartComponent, TranslocoDirective, MatIconButton, MatIcon, MatTooltip],
	templateUrl: './parameter-value-gantt.component.html',
	styleUrls: ['./parameter-value-gantt.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParameterValueGanttComponent implements AfterViewInit, OnDestroy {
	private elRef = inject(ElementRef);
	icons = inject(Icons);

	// --- SIGNAL-BASED INPUTS ---
	chartData = input.required<GanttRow[]>();
	axisPosition = input<'top' | 'bottom' | 'none'>('top');
	rowHeight = input<number>(70);
	axisOffset = input<number>(16);
	barHeight = input<number>(24);
	showAddBtn = input<boolean>(true);
	showLabel = input<boolean>(true);
	reserveActionsSpace = input<boolean>(true);
	parameterValueType = input<ParameterValueType>();
	allowSegmentClick = input<boolean>(true);
	readonly barTextStyle = input<BarTextStyle>({});
	enableAxisDrag = input<boolean>(false);
	disableAnimation = input<boolean>(false);
	readonly rowActionClick = output<GanttClickEvent>();
	readonly ganttSegmentClick = output<GanttClickEvent>();
	readonly dateRangeShift = output<{
		startDate: Date;
		endDate: Date;
	}>();
	readonly dragStart = output<void>();
	readonly dragEnd = output<void>();
	private readonly margin = { top: 20, right: 20, bottom: 20, left: 20 };

	private readonly labelColumnWidth = signal(175);
	private readonly actionsColumnWidth = 48;
	public gridTemplateColumns = computed(() => {
		const labelCol = this.showLabel() ? `${this.labelColumnWidth()}px` : '';
		const actionsCol = this.reserveActionsSpace() ? `${this.actionsColumnWidth}px` : '';
		return `${labelCol} 1fr ${actionsCol}`.trim();
	});

	viewStartDate = input<Date | null | undefined>(null);
	viewEndDate = input<Date | null | undefined>(null);

	private scale: TimeScale = d3.scaleTime();

	private resizeObserver!: ResizeObserver;

	private currentCategory: string = '';

	private chartAreaWidth: WritableSignal<number> = signal(0);

	public xScale = computed(() => {
		const data = this.chartData();
		const startDate = this.viewStartDate();
		const endDate = this.viewEndDate();
		const width = this.chartAreaWidth();

		if (width <= 0) {
			return d3.scaleTime();
		}

		let minDate: Date;
		let maxDate: Date;
		if (startDate && endDate) {
			minDate = startDate;
			maxDate = endDate;
		} else {
			const allSegments = data.flatMap((row) => row.segments);
			minDate = (d3.min(allSegments, (d) => d.from) as Date) || new Date();
			maxDate = (d3.max(allSegments, (d) => d.to) as Date) || d3.timeDay.offset(minDate, 1);
		}
		const paddedMinDate = d3.timeDay.offset(minDate, -1);
		const paddedMaxDate = d3.timeDay.offset(maxDate, 1);

		const range = [this.margin.left, width - this.margin.right];

		return d3.scaleTime().domain([paddedMinDate, paddedMaxDate]).range(range);
	});

	constructor() {
		effect(() => {
			const data = this.chartData();
			const startDate = this.viewStartDate();
			const endDate = this.viewEndDate();

			if (!data) return;

			let minDate: Date;
			let maxDate: Date;

			if (startDate && endDate) {
				minDate = startDate;
				maxDate = endDate;
			} else {
				const allSegments = data.flatMap((row) => row.segments);
				if (allSegments.length === 0) {
					// Set a default domain if there's no data
					minDate = new Date();
					maxDate = d3.timeDay.offset(minDate, 1);
				} else {
					minDate = d3.min(allSegments, (d) => d.from) as Date;
					maxDate = d3.max(allSegments, (d) => d.to) as Date;
				}
			}

			const paddedMinDate = d3.timeDay.offset(minDate, -1);
			const paddedMaxDate = d3.timeDay.offset(maxDate, 1);

			this.scale.domain([paddedMinDate, paddedMaxDate]);
		});
	}

	ngAfterViewInit() {
		this.resizeObserver = new ResizeObserver(() => {
			const hostWidth = this.elRef.nativeElement.clientWidth;
			if (hostWidth > 0) {
				const labelWidth = this.showLabel() ? this.labelColumnWidth() : 0;
				const actionsWidth = this.reserveActionsSpace() ? this.actionsColumnWidth : 0;
				this.chartAreaWidth.set(hostWidth - labelWidth - actionsWidth);
			}
		});
		this.resizeObserver.observe(this.elRef.nativeElement);
	}
	ngOnDestroy() {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
	}
	shouldShowAxis(index: number): boolean {
		// Read the signal for axisPosition
		if (this.axisPosition() === 'top' && index === 0) return true;
		if (this.axisPosition() === 'bottom' && index === this.chartData().length - 1) return true;
		return false;
	}

	public onRowActionClick(row: GanttRow, parameterValueType: ParameterValueType): void {
		this.rowActionClick.emit({ row: row, parameterValueType: parameterValueType });
	}

	public handleSegmentClick(segment: GanttSegment, row: GanttRow, parameterValueType: ParameterValueType): void {
		if (this.allowSegmentClick()) {
			this.ganttSegmentClick.emit({ segment, row, parameterValueType });
		}
	}

	public handleDateRangeShift(event: { startDate: Date; endDate: Date }): void {
		this.dateRangeShift.emit(event);
	}

	public handleDragStart(): void {
		this.dragStart.emit();
	}

	public handleDragEnd(): void {
		this.dragEnd.emit();
	}

	protected readonly console = console;
}
